import React, { lazy, Suspense } from "react";
import { Navigate } from "react-router";
import { LoadingProgressBar } from "@rentablo/components";
import {
  ROLE_BANKING_API_LOGS_VIEWER,
  ROLE_CROSS_MANDATOR_BANKING_API_LOGS_VIEWER,
  ROLE_CROSS_MANDATOR_STATISTICS_VIEWER,
  ROLE_FINAPI_MANDATOR_ADMIN,
  ROLE_MANDATOR_ADMIN,
  ROLE_MANDATOR_LEVEL_STATISTICS_VIEWER,
  ROLE_MASTER_DATA_ADMIN,
  ROLE_MULTIBANKING_API_ADMIN,
} from "@constants";
import {
  API_TOKEN_IMPERSONATION_ROLES,
  USER_VIEW_ROLES,
} from "@features/admin/userManagement/permissions";
import { INVOICING_AND_COMPLIANCE_ROLES } from "@features/admin/invoicing/permissions";
import { KPI_ROLES } from "@features/admin/kpis/permissions";
import { ProtectedRoute } from "../routes.tsx";

const AdminDashboardPage = lazy(() =>
  import("@features/admin/AdminDashboardPage.tsx").then((module) => ({
    default: module.AdminDashboardPage,
  }))
);

const AdminCapitalMeasuresSectionLayout = lazy(() =>
  import("@features/admin/sections/AdminCapitalMeasuresSectionLayout.tsx").then(
    (module) => ({ default: module.AdminCapitalMeasuresSectionLayout })
  )
);

const AdminSecuritiesSectionLayout = lazy(() =>
  import("@features/admin/sections/AdminSecuritiesSectionLayout.tsx").then(
    (module) => ({ default: module.AdminSecuritiesSectionLayout })
  )
);

const AdminBankingSectionLayout = lazy(() =>
  import("@features/admin/sections/AdminBankingSectionLayout.tsx").then(
    (module) => ({ default: module.AdminBankingSectionLayout })
  )
);

const AdminOtherSectionLayout = lazy(() =>
  import("@features/admin/sections/AdminOtherSectionLayout.tsx").then(
    (module) => ({ default: module.AdminOtherSectionLayout })
  )
);

const AdminFinApiSectionLayout = lazy(() =>
  import("@features/admin/sections/AdminFinApiSectionLayout.tsx").then(
    (module) => ({ default: module.AdminFinApiSectionLayout })
  )
);

const AdminCurrenciesSectionLayout = lazy(() =>
  import("@features/admin/sections/AdminCurrenciesSectionLayout.tsx").then(
    (module) => ({ default: module.AdminCurrenciesSectionLayout })
  )
);

const AdminInvoicingSectionLayout = lazy(() =>
  import("@features/admin/sections/AdminInvoicingSectionLayout.tsx").then(
    (module) => ({ default: module.AdminInvoicingSectionLayout })
  )
);

const AdminKpiSectionLayout = lazy(() =>
  import("@features/admin/sections/AdminKpiSectionLayout.tsx").then(
    (module) => ({ default: module.AdminKpiSectionLayout })
  )
);

const AdminMandatorSectionLayout = lazy(() =>
  import("@features/admin/sections/AdminMandatorSectionLayout.tsx").then(
    (module) => ({ default: module.AdminMandatorSectionLayout })
  )
);

const MandatorConfigurationPage = lazy(() =>
  import(
    "@features/admin/mandators/configuration/MandatorConfigurationPage.tsx"
  ).then((module) => ({ default: module.MandatorConfigurationPage }))
);

const AdminUserManagementSectionLayout = lazy(() =>
  import("@features/admin/sections/AdminUserManagementSectionLayout.tsx").then(
    (module) => ({ default: module.AdminUserManagementSectionLayout })
  )
);

const UserDetailPage = lazy(() =>
  import("@features/admin/userManagement/UserDetailPage.tsx").then(
    (module) => ({
      default: module.UserDetailPage,
    })
  )
);

const SplitAddAndEditPage = lazy(() =>
  import(
    "@features/admin/capitalMeasures/splits/split/SplitAddAndEditPage.tsx"
  ).then((module) => ({ default: module.SplitAddAndEditPage }))
);

const InterfaceAddAndEditPage = lazy(() =>
  import(
    "@features/admin/interfaces/interface/InterfaceAddAndEditPage.tsx"
  ).then((module) => ({ default: module.InterfaceAddAndEditPage }))
);

const s = (element: React.ReactElement) => (
  <Suspense fallback={<LoadingProgressBar />}>{element}</Suspense>
);

export default [
  {
    path: "admin",
    element: (
      <ProtectedRoute
        roles={[
          ROLE_MASTER_DATA_ADMIN,
          ROLE_MULTIBANKING_API_ADMIN,
          ROLE_BANKING_API_LOGS_VIEWER,
          ROLE_CROSS_MANDATOR_BANKING_API_LOGS_VIEWER,
          ROLE_CROSS_MANDATOR_STATISTICS_VIEWER,
          ROLE_MANDATOR_LEVEL_STATISTICS_VIEWER,
          ROLE_FINAPI_MANDATOR_ADMIN,
          ...USER_VIEW_ROLES,
          ...API_TOKEN_IMPERSONATION_ROLES,
          ...INVOICING_AND_COMPLIANCE_ROLES,
        ]}
      />
    ),
    children: [
      {
        index: true,
        element: s(<AdminDashboardPage />),
      },
      {
        path: "capitalMeasures/*",
        element: (
          <ProtectedRoute roles={[ROLE_MASTER_DATA_ADMIN]}>
            {s(<AdminCapitalMeasuresSectionLayout />)}
          </ProtectedRoute>
        ),
      },
      {
        path: "securities/*",
        element: (
          <ProtectedRoute roles={[ROLE_MASTER_DATA_ADMIN]}>
            {s(<AdminSecuritiesSectionLayout />)}
          </ProtectedRoute>
        ),
      },
      {
        path: "securities/cryptoRefresh",
        element: <Navigate to="/admin/currencies/crypto" replace />,
      },
      {
        path: "currencies/*",
        element: (
          <ProtectedRoute roles={[ROLE_MASTER_DATA_ADMIN]}>
            {s(<AdminCurrenciesSectionLayout />)}
          </ProtectedRoute>
        ),
      },
      {
        path: "banking/*",
        element: (
          <ProtectedRoute
            roles={[
              ROLE_MASTER_DATA_ADMIN,
              ROLE_MULTIBANKING_API_ADMIN,
              ROLE_BANKING_API_LOGS_VIEWER,
              ROLE_CROSS_MANDATOR_BANKING_API_LOGS_VIEWER,
            ]}
          >
            {s(<AdminBankingSectionLayout />)}
          </ProtectedRoute>
        ),
      },
      {
        path: "other/*",
        element: (
          <ProtectedRoute
            roles={[
              ROLE_MASTER_DATA_ADMIN,
              ROLE_CROSS_MANDATOR_STATISTICS_VIEWER,
              ROLE_MANDATOR_LEVEL_STATISTICS_VIEWER,
            ]}
          >
            {s(<AdminOtherSectionLayout />)}
          </ProtectedRoute>
        ),
      },
      {
        path: "finApi/*",
        element: (
          <ProtectedRoute roles={[ROLE_FINAPI_MANDATOR_ADMIN]}>
            {s(<AdminFinApiSectionLayout />)}
          </ProtectedRoute>
        ),
      },
      {
        path: "mandators/*",
        element: (
          <ProtectedRoute roles={[ROLE_MANDATOR_ADMIN]}>
            {s(<AdminMandatorSectionLayout />)}
          </ProtectedRoute>
        ),
      },
      {
        path: "mandators/detail/:mandatorId",
        element: (
          <ProtectedRoute roles={[ROLE_MANDATOR_ADMIN]}>
            {s(<MandatorConfigurationPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: "invoicing/*",
        element: (
          <ProtectedRoute roles={INVOICING_AND_COMPLIANCE_ROLES}>
            {s(<AdminInvoicingSectionLayout />)}
          </ProtectedRoute>
        ),
      },
      {
        path: "kpis/*",
        element: (
          <ProtectedRoute roles={KPI_ROLES}>
            {s(<AdminKpiSectionLayout />)}
          </ProtectedRoute>
        ),
      },
      {
        path: "users/*",
        element: (
          <ProtectedRoute
            roles={[...USER_VIEW_ROLES, ...API_TOKEN_IMPERSONATION_ROLES]}
          >
            {s(<AdminUserManagementSectionLayout />)}
          </ProtectedRoute>
        ),
      },
      {
        path: "users/detail/:userId",
        element: (
          <ProtectedRoute roles={USER_VIEW_ROLES}>
            {s(<UserDetailPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: "capitalMeasures/splits/add",
        element: (
          <ProtectedRoute roles={[ROLE_MASTER_DATA_ADMIN]}>
            {s(<SplitAddAndEditPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: "capitalMeasures/splits/:splitId/edit",
        element: (
          <ProtectedRoute roles={[ROLE_MASTER_DATA_ADMIN]}>
            {s(<SplitAddAndEditPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: "other/interfaces/add",
        element: (
          <ProtectedRoute roles={[ROLE_MASTER_DATA_ADMIN]}>
            {s(<InterfaceAddAndEditPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: "other/interfaces/:bicGroup/edit",
        element: (
          <ProtectedRoute roles={[ROLE_MASTER_DATA_ADMIN]}>
            {s(<InterfaceAddAndEditPage />)}
          </ProtectedRoute>
        ),
      },
    ],
  },
];
